import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UsersService } from '../../../shared/services/users/users.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqService } from 'src/app/shared/services/qeq/qeq.service';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { ExcelService } from '../../../shared/services/excel/excel.service';
import { RestaurantStateService } from '../../../shared/services/restaurant/restaurant-state.service';
import { RestaurantFilterBarComponent } from '../../../shared/components/restaurant/restaurant-filter-bar/restaurant-filter-bar.component';
import { FilterBarConfig } from '../../../shared/interfaces/restaurant-shared.interface';
import { ExportExcelButtonComponent } from '../../../shared/components/export-excel-button/export-excel-button.component';
import { EmployeesMealStatsResponse } from 'src/app/shared/interfaces/restaurant.interface';

@Component({
  selector: 'app-consumption-restaurant',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    MatSlideToggleModule,
    LoaderComponent,
    QeqPipe,
    RestaurantFilterBarComponent,
    ExportExcelButtonComponent,
  ],
  templateUrl: './consumption-restaurant.component.html',
  styleUrls: [
    './consumption-restaurant.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ConsumptionRestaurantComponent implements OnDestroy {
  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  isActive = true;

  // Flag to track if QeQ data is loaded
  public qeqDataLoaded = false;

  // Store all employees data and filtered data separately
  private allEmployeesData: any[] = [];
  private filteredEmployeesData: any[] = [];

  private employeeStatsResponse: EmployeesMealStatsResponse = {
    start_date: '',
    end_date: '',
    employees: [],
  };

  constructor(
    private restaurantService: RestaurantService,
    private usersService: UsersService,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService,
    private qeqService: QeqService, // <-- Inject QeqService
    private excelService: ExcelService<any>,
    private restaurantStateService: RestaurantStateService
  ) {}

  // Filter bar properties
  searchText: string = '';
  filterBarConfig: FilterBarConfig = {
    hasSearch: true,
    searchPlaceholder: 'Buscar por nombre o ID...',
    hasBackButton: false,
    hasDateRange: true,
    filters: [
      { value: 'month', label: 'Mes' },
      { value: 'yesterday', label: 'Ayer' },
      { value: 'today', label: 'Hoy' },
      { value: 'intervalo', label: 'Intérvalo' },
    ],
    paginatorOptions: [25, 50, 100],
  };

  get isLoading() {
    return this.loaderService.getLoader();
  }

  selectedFilter: string = 'month';

  // Add date range for interval filter
  startDate: Date | null = null;
  endDate: Date | null = null;

  onFilterChange(filter: string) {
    this.selectedFilter = filter;
    this.saveCurrentState();
    this.loadEmployeeData();
  }

  onDateRangeChange(dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) {
    this.startDate = dateRange.startDate;
    this.endDate = dateRange.endDate;
    if (this.selectedFilter === 'intervalo' && this.startDate && this.endDate) {
      this.saveCurrentState();
      this.loadEmployeeData();
    }
  }

  // Helper method to format date for API (YYYY-MM-DD)
  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Method to apply filters (only search now)
  applyFilters() {
    const filterValue = JSON.stringify({
      searchText: this.searchText.toLowerCase(),
    });
    this.dataSource.filter = filterValue;
  }

  onSearchChange(searchText: string) {
    this.searchText = searchText;
    this.saveCurrentState();
    this.applyFilters();
  }

  onToggleChange(isActive: boolean) {
    this.isActive = isActive;
    // Switch between filtered and all data based on toggle state
    this.dataSource.data = this.isActive
      ? this.filteredEmployeesData
      : this.allEmployeesData;

    // Ensure paginator is connected after data change
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }

    // Reapply any existing search filters
    this.applyFilters();
    this.saveCurrentState();
  }

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = [
    'users',
    'id-users',
    'breakfast-completed',
    'lunch-completed',
    'total-spent',
    'total-company-spent',
    'total-no-charged',
    // 'charge',
    'actions',
  ];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(RestaurantFilterBarComponent)
  filterBarComponent!: RestaurantFilterBarComponent;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;

    // Use setTimeout to ensure the filter bar component is fully initialized
    setTimeout(() => {
      if (this.filterBarComponent) {
        this.paginator = this.filterBarComponent.getPaginator();
        this.dataSource.paginator = this.paginator;
      }
    });
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta noticia?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          // return this.newsService.deleteNews(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (user) => user.id !== id
        );
      }
    });
  }

  myroles: any = {}; // Change from array to object

  ngOnInit() {
    // Setup navigation listener
    this.setupNavigationListener();

    // Restore state from service
    this.restoreState();

    // Load roles and QeQ data before employee stats
    this.loadRoles();
    this.loadQeqDataAndThenEmployees();
  }

  // New method to ensure sequential loading
  loadQeqDataAndThenEmployees() {
    const qeqSub = this.qeqService.getQeQ().subscribe({
      next: (qeqData) => {
        this.qeqDataLoaded = true;
        this.loadEmployeeData();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.qeqDataLoaded = false;
        this.loadEmployeeData();
      },
    });
    this.subscriptions.push(qeqSub);
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    /* this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.router.url.includes('/dining/users')) {
          // Clear existing subscriptions before reloading data
          this.clearSubscriptions();

          // Reload data
          this.loadRoles();
          this.loadEmployeeData();
        }

        this.cdr.detectChanges();
      });*/
  }
  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'general',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      // console.log(
      //   'No user roles found in storage or no permissions for Comedor'
      //  );
    }
  }

  loadEmployeeData() {
    // Create request body based on filter selection
    const body: any = { filtered_stats: this.selectedFilter };

    // Add date range parameters for interval filter
    if (this.selectedFilter === 'intervalo' && this.startDate && this.endDate) {
      body.start_date = this.formatDateForApi(this.startDate);
      body.end_date = this.formatDateForApi(this.endDate);
    }

    // Treat 'month' as an intervalo request (first day of month -> today)
    if (this.selectedFilter === 'month') {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      body.filtered_stats = 'intervalo'; // override to send as intervalo
      body.start_date = this.formatDateForApi(firstDay);
      body.end_date = this.formatDateForApi(now);
    }

    const employeesSub = this.restaurantService
      .getEmployeeStats(body)
      .subscribe({
        next: (response: any) => {
          this.employeeStatsResponse = response;
          // Ensure response is an array
          let employees = Array.isArray(response) ? response : [];

          // If response is an object with a data property that's an array, use that instead
          if (
            !Array.isArray(response) &&
            response &&
            Array.isArray(response.data)
          ) {
            employees = response.data;
          }
          // Support "employees" property (actual API shape)
          if (
            !Array.isArray(response) &&
            response &&
            Array.isArray(response.employees)
          ) {
            employees = response.employees;
          }

          // Fallback log if nothing found
          if (!employees.length) {
            console.warn(
              'Employee stats response had no usable array (expected array, data[], or employees[]). Raw response:',
              response
            );
          }

          // Get QeQ cache for name mapping
          const cached = this.qeqDataLoaded
            ? this.qeqService['getQeqFromStorage']?.()
            : null;

          // Sort: first by total_spent DESC, then by name ASC for ties (total_spent === 0)
          employees = employees.sort((a: any, b: any) => {
            if (b.total_spent !== a.total_spent) {
              return b.total_spent - a.total_spent;
            }
            // If total_spent is equal, sort by name (from QeQ)
            const nameA =
              cached && cached.data
                ? cached.data.find(
                    (u: any) => u.personnelnumber === a.employee_id
                  )?.name || a.employee_id
                : a.employee_id;
            const nameB =
              cached && cached.data
                ? cached.data.find(
                    (u: any) => u.personnelnumber === b.employee_id
                  )?.name || b.employee_id
                : b.employee_id;
            return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
          });

          // Store all employees data
          this.allEmployeesData = employees;

          // Filter employees with consumption > 0 for filteredEmployeesData
          this.filteredEmployeesData = employees.filter((employee: any) => {
            return (
              employee.total_spent > 0 ||
              employee.total_completed > 0 ||
              employee.total_reservations > 0 ||
              employee.total_no_charged > 0
            );
          });

          // Set dataSource based on current toggle state
          this.dataSource.data = this.isActive
            ? this.filteredEmployeesData
            : this.allEmployeesData;

          // Ensure paginator is connected after data is set
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }

          this.setupFilterPredicate();

          // Restore view state after data is loaded
          setTimeout(() => {
            this.restoreViewState();
          }, 50);

          this.loaderService.hideLoader();
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error fetching employees:', error);
        },
      });

    this.subscriptions.push(employeesSub);
  }

  setupFilterPredicate() {
    // Set up the filter predicate for search only
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter);
      let searchMatch = true;

      if (filterObj.searchText && filterObj.searchText.trim() !== '') {
        const searchText = filterObj.searchText.toLowerCase();

        // Ensure QeQ data is loaded before trying to use it for filtering
        const cached = this.qeqDataLoaded
          ? this.qeqService['getQeqFromStorage']?.()
          : null;

        // Find employee by external_id
        const employeeName =
          cached && cached.data
            ? cached.data.find(
                (u: any) => u.personnelnumber === data.employee_id
              )?.name || ''
            : '';

        searchMatch =
          (data.employee_id &&
            data.employee_id.toLowerCase().includes(searchText)) ||
          (data.employee_name &&
            data.employee_name.toLowerCase().includes(searchText)) ||
          (employeeName && employeeName.toLowerCase().includes(searchText));
      }

      return searchMatch;
    };
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.clearSubscriptions();

    // Also clean up navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  onExportClick() {
    this.exportToExcel();
  }

  exportToExcel() {
    // Export whatever is currently displayed in the table (respecting the toggle state)
    const dataToExport = this.dataSource.data.map((item) => {
      const cached = this.qeqDataLoaded
        ? this.qeqService['getQeqFromStorage']?.()
        : null;
      const employeeName =
        cached && cached.data
          ? cached.data.find((u: any) => u.personnelnumber === item.employee_id)
              ?.name || item.employee_name
          : item.employee_name;

      return {
        Usuario: employeeName,
        'ID Empleado': item.employee_id,
        Inicio: this.employeeStatsResponse.start_date
          ? this.employeeStatsResponse.start_date
          : '',
        Fin: this.employeeStatsResponse.end_date
          ? this.employeeStatsResponse.end_date
          : '',
        Desayunos: item.breakfast_completed,
        Comidas: item.lunch_completed,
        'Coste desayuno': `${item.breakfast_spent}€`,
        'Coste comida': `${item.lunch_spent}€`,
        'Coste empresa desayuno': `${item.breakfast_company_spent}€`,
        'Coste empresa comida': `${item.lunch_company_spent}€`,
        Total: `${item.total_spent}€`,
        'Total empresa': `${item.total_company_spent}€`,
        'Total no cargado': item.total_no_charged,
        'Desayuno no cargado': item.breakfast_no_charged,
        'Comida no cargada': item.lunch_no_charged,
      };
    });

    const startDateStr =
      this.employeeStatsResponse.start_date ||
      this.formatDateForApi(new Date());
    const endDateStr =
      this.employeeStatsResponse.end_date || this.formatDateForApi(new Date());
    const fileName = `consumos-${startDateStr}-${endDateStr}`;
    this.excelService.exportExcel(dataToExport, fileName);
  }

  seePunches(element: any) {
    // Save current state before navigation
    this.saveCurrentState();

    this.router.navigate(['/home/dining/punches'], {
      queryParams: { employeeId: element.employee_id },
    });
  }

  private restoreState() {
    const savedState = this.restaurantStateService.getCurrentConsumptionState();

    this.searchText = savedState.searchText;
    this.selectedFilter = savedState.selectedFilter;
    this.startDate = savedState.startDate;
    this.endDate = savedState.endDate;
    this.isActive = savedState.isActive;
  }

  private saveCurrentState() {
    // Get current scroll position
    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    // Get current paginator state
    const paginatorPageIndex = this.paginator?.pageIndex || 0;
    const paginatorPageSize = this.paginator?.pageSize || 10;

    // Get current sort state
    const sortActive = this.sort?.active || '';
    const sortDirection = this.sort?.direction || '';

    this.restaurantStateService.updateConsumptionState({
      searchText: this.searchText,
      selectedFilter: this.selectedFilter,
      startDate: this.startDate,
      endDate: this.endDate,
      paginatorPageIndex,
      paginatorPageSize,
      sortActive,
      sortDirection,
      scrollPosition,
      isActive: this.isActive,
    });
  }

  private restoreViewState() {
    const savedState = this.restaurantStateService.getCurrentConsumptionState();

    // Restore paginator state
    if (this.paginator) {
      this.paginator.pageIndex = savedState.paginatorPageIndex;
      this.paginator.pageSize = savedState.paginatorPageSize;
    }

    // Restore sort state
    if (this.sort && savedState.sortActive) {
      this.sort.active = savedState.sortActive;
      this.sort.direction = savedState.sortDirection;
    }

    // Apply saved search filter
    if (savedState.searchText) {
      this.applyFilters();
    }

    // Restore scroll position after a brief delay to ensure DOM is ready
    setTimeout(() => {
      if (savedState.scrollPosition > 0) {
        window.scrollTo(0, savedState.scrollPosition);
      }
    }, 100);
  }
}
